import { useNavigate, useLocation, useSearchParams } from 'react-router-dom';
import { DataTable, DataTableSearchConfig } from '@/components/DataTable';
import { useEffect, useMemo, useState } from 'react';
import { OneLineText } from '@/components/OneLineText';
import { ColumnDef } from '@tanstack/react-table';
import { Download, Edit3, Ellipsis, MessageSquarePlus } from 'lucide-react';
import { deliveryService, useDeliveryList } from '@/services/deliveryService';
import { DeliveryListItem, DeliveryListQueryParams, DeliveryListResponse } from '@/types/delivery.types';
import { convertISODateToMMDDYYYY, downloadFilefromBuffer } from '@/lib/utils';
import { FormProvider, useForm } from 'react-hook-form';
import { toast } from '@/components/ui/use-toast';
import { displayError } from '@/lib/error';
import { DELIVERY_STATUS_OPTIONS } from '@/utils/constant';
import { useFilterStore } from '@/stores/filterStore';
import { AdvanceFilterFieldType, AdvanceFilterOperator } from '@/types/types';
import { useTeamGetAll } from '@/services/teamService';

import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { hasFeature } from '@/services/featureService';
import DeliveryCommentsDialog from './DeliveryCommentsDialog';
import { DeliveryAdvanceFilters } from './DeliveryAdvanceFilters';
import { DeliveryDetails } from './deliveryDetails/DeliveryDetails';
import type { DeliveryPanelNavigationState } from './DeliveryUpdateManage';
import { UseQueryOptions, useQueryClient } from '@tanstack/react-query';
import { ServiceSwitcher } from '@/components/ServiceSwitcher';
import { CancellationReasonDialog } from './CancellationReasonDialog';
import { isAdminOrSupervisor } from '@/services/userService';
import { useUserStore } from '@/stores/user';

const DELIVERY_ID_SEARCH_FIELDS = new Set(['workOrderNumber', 'clientId', 'caseNumber', 'status', 'teamId']);
const DELIVERY_TEXT_SEARCH_FIELDS = new Set([
  'driverName',
  'caseName',
  'fileNumber',
  'clientContact',
  'pickupAddress',
  'deliveryAddress',
  'deliveryPersonName',
  'pickupPersonName',
]);

const DeliveryList = () => {
  const filterStore = useFilterStore((state) => state);
  const user = useUserStore((state) => state.user);

  const canAssignMe = useMemo(() => {
    if (!user) return false;
    if (isAdminOrSupervisor(user)) return true;

    // Check if operator and belongs to DELIVERY team
    const isOperator = user.roleId === 'OPERATOR';
    const belongsToDelivery = user.teams?.some((team) => team.key === 'DELIVERY') ?? false;

    return isOperator && belongsToDelivery;
  }, [user]);

  const { deliveryFilters, setDeliveryFilters } = filterStore;
  const methods = useForm();
  const navigate = useNavigate();
  const location = useLocation();
  const [searchParams, setSearchParams] = useSearchParams();
  const queryClient = useQueryClient();
  const [selectedWorkOrderId, setSelectedWorkOrderId] = useState<string | null>(null);
  const [selectedDelivery, setSelectedDelivery] = useState<{ id: string; clientName: string | null } | null>(null);
  const [openCommentSection, setOpenCommentSection] = useState(false);
  const [isAdvanceFilterOpen, setIsAdvanceFilterOpen] = useState(false);

  const [refreshKey, setRefreshKey] = useState(0);
  const { data: teams } = useTeamGetAll();

  const [cancellationDialog, setCancellationDialog] = useState<{
    open: boolean;
    id: string | null;
    type: 'WORK_ORDER' | 'INTAKE' | null;
  }>({ open: false, id: null, type: null });
  const [isCancelling, setIsCancelling] = useState(false);

  useEffect(() => {
    const state = location.state as DeliveryPanelNavigationState | null;
    if (state?.openDeliveryId) {
      setSelectedDelivery({
        id: state.openDeliveryId,
        clientName: state.clientName ?? null,
      });
      setSearchParams({ intakeId: state.openDeliveryId });
      navigate(location.pathname, { replace: true, state: null });
    }
  }, [location.state, location.pathname, navigate, setSearchParams]);

  useEffect(() => {
    const intakeId = searchParams.get('intakeId');
    if (intakeId) {
      setSelectedDelivery((prev) =>
        prev?.id === intakeId ? prev : { id: intakeId, clientName: prev?.clientName ?? null }
      );
    }
  }, [searchParams]);

  // Wrapper function for DataTable apiGet prop
  const getDeliveryListData = (
    params?: unknown,
    options?: { query?: UseQueryOptions<DeliveryListResponse> }
  ) => {
    const { filters, pageNo, pageSize, sortField, sortDirection } = (params ?? {}) as DeliveryListQueryParams;
    return useDeliveryList({ pageNo, pageSize, filters, sortField, sortDirection }, options);
  };

  const handleRowClick = (row: DeliveryListItem, e: React.MouseEvent) => {
    const targetElement = e.target as HTMLElement;
    const closestActionElement = targetElement.closest('.action');
    // If clicking on an action button or modal, don't open the side panel
    if (closestActionElement !== null || targetElement.classList.contains('modal')) {
      return;
    }
    // Check if user has permission to view details
    if (hasFeature('delivery.read') && row.intakeId) {
      setSearchParams({ intakeId: row.intakeId });
      setSelectedDelivery({ id: row.intakeId, clientName: row.clientName });
    }
  };

  const statusClassMap: Record<string, string> = {
    Draft: 'bg-[#F5F5F5] text-[#676767] hover:bg-[#F5F5F5]',
    Unassigned: 'bg-[#E2E8F0] text-[#475569] hover:bg-[#E2E8F0]',
    Assigned: 'bg-[#EDF5FF] font-medium text-primary hover:bg-[#EDF5FF]',
    OutForDelivery: 'bg-[#FEF08A] text-[#854D0E] hover:bg-[#FEF08A]',
    Delivered: 'bg-[#E3F2FD] text-[#1565C0] hover:bg-[#E3F2FD]',
    Billed: 'bg-[#F3E5F5] text-[#6A1B9A] hover:bg-[#F3E5F5]',
    Rescheduled: 'bg-[#FFEEBF] text-[#6C4A09] hover:bg-[#FFEEBF]',
    Cancelled: 'bg-[#FFEBEE] text-[#C62828] hover:bg-[#FFEBEE]',
  };

  const teamsOptions = useMemo(() => {
    return (
      teams?.teams.map((team) => ({
        label: team.name,
        value: team.id,
        team: team,
      })) ?? []
    );
  }, [teams]);

  const deliverySearchFields: DataTableSearchConfig[] = useMemo(
    () => [
      {
        label: 'Search Work Order Number',
        name: 'workOrderNumber',
        placeholder: 'Search Work Order Number',
      },
      {
        label: 'Search Client ID',
        placeholder: 'Search Client ID',
        name: 'clientId',
      },
      {
        label: 'Search Case Number',
        placeholder: 'Search Case Number',
        name: 'caseNumber',
      },
      {
        label: 'Search Driver Name',
        placeholder: 'Search Driver Name',
        name: 'driverName',
      },
      {
        label: 'Status',
        placeholder: 'Select Status',
        name: 'status',
        fieldType: 'SELECT',
        options: DELIVERY_STATUS_OPTIONS.filter((opt) => opt.value !== 'Converted'),
      },
      {
        label: 'Team',
        placeholder: 'Select Team',
        name: 'teamId',
        defaultValue: 'DELIVERY',
        stickyItem: {
          label: 'Delivery',
          value: 'DELIVERY',
        },
        options: teamsOptions
          .filter((team) => team.label.toLowerCase() !== 'delivery')
          .map((team) => ({ label: team.label, value: team.value })),
        fieldType: 'SELECT',
      },
    ],
    [teamsOptions]
  );

  useEffect(() => {
    if (deliveryFilters.length === 0) {
      setDeliveryFilters([
        {
          field: 'teamId',
          operator: AdvanceFilterOperator.EQUALS,
          value: 'DELIVERY',
          fieldType: AdvanceFilterFieldType.ID,
        },
      ]);
    }
  }, []);

  const handleConvertToWorkOrder = async (intakeId: string) => {
    if (!hasFeature('delivery.workOrder.create')) {
      toast({
        variant: 'destructive',
        description: 'You do not have permission to convert intake forms.',
        duration: 3000,
      });
      return;
    }

    const { error } = await deliveryService.convertToWorkOrder(intakeId);
    if (error) {
      displayError(error, 5000);
    } else {
      toast({
        variant: 'default',
        description: 'Intake form converted to work order successfully.',
        duration: 3000,
      });
      // Invalidate detail cache so the side panel re-fetches as a work order
      void queryClient.invalidateQueries({ queryKey: ['delivery-detail', intakeId] });
      // Refresh the list
      setRefreshKey((prev) => prev + 1);
    }
  };

  const handleAssignToMe = async (workOrderId: string | undefined, intakeId: string | undefined) => {
    // Validation: Ensure exactly one parameter is provided
    if (!workOrderId && !intakeId) {
      console.error('handleAssignToMe: Neither workOrderId nor intakeId provided');
      return;
    }

    if (workOrderId && intakeId) {
      console.error('handleAssignToMe: Both workOrderId and intakeId provided');
      return;
    }

    if (workOrderId) {
      // Assign work order
      const { error } = await deliveryService.assignToMe(workOrderId);
      if (error) {
        displayError(error, 5000);
      } else {
        toast({
          variant: 'default',
          description: 'Work order assigned to you successfully.',
          duration: 3000,
        });
        setRefreshKey((prev) => prev + 1);
      }
    } else if (intakeId) {
      // Assign intake form
      const { error } = await deliveryService.assignIntakeToMe(intakeId);
      if (error) {
        displayError(error, 5000);
      } else {
        toast({
          variant: 'default',
          description: 'Intake form assigned to you successfully.',
          duration: 3000,
        });
        setRefreshKey((prev) => prev + 1);
      }
    }
  };

  const handleCancelDelivery = (workOrderId: string | undefined, intakeId: string | undefined) => {
    if (!hasFeature('delivery.edit.status')) {
      toast({
        variant: 'destructive',
        description: 'You do not have permission to cancel deliveries.',
        duration: 3000,
      });
      return;
    }

    // Validation: Ensure exactly one parameter is provided
    if (!workOrderId && !intakeId) {
      console.error('handleCancelDelivery: Neither workOrderId nor intakeId provided');
      return;
    }

    // if (workOrderId && intakeId) {
    //   console.error('handleCancelDelivery: Both workOrderId and intakeId provided');
    //   return;
    // }

    if (workOrderId) {
      setCancellationDialog({ open: true, id: workOrderId, type: 'WORK_ORDER' });
    } else if (intakeId) {
      setCancellationDialog({ open: true, id: intakeId, type: 'INTAKE' });
    }
  };

  const onConfirmCancellation = async (reason: string) => {
    if (!cancellationDialog.id || !cancellationDialog.type) return;

    setIsCancelling(true);

    let response;
    if (cancellationDialog.type === 'WORK_ORDER') {
      response = await deliveryService.cancelWorkOrder(cancellationDialog.id, reason);
    } else {
      response = await deliveryService.cancelIntakeForm(cancellationDialog.id, reason);
    }

    if (response.error) {
      displayError(response.error, 5000);
      setIsCancelling(false);
    } else {
      toast({
        variant: 'default',
        description: `${cancellationDialog.type === 'WORK_ORDER' ? 'Work order' : 'Intake form'} canceled successfully.`,
        duration: 3000,
      });
      setRefreshKey((prev) => prev + 1);
      setCancellationDialog({ open: false, id: null, type: null });
      setIsCancelling(false);
    }
  };

  const handleRescheduleWorkOrder = () => {
    if (!hasFeature('delivery.workOrder.reschedule')) {
      toast({
        variant: 'destructive',
        description: 'You do not have permission to reschedule work orders.',
        duration: 3000,
      });
      return;
    }

    // TODO: Open a date picker dialog to select new delivery date
    // For now, just show a toast
    toast({
      variant: 'default',
      description: 'Reschedule functionality not yet implemented.',
      duration: 3000,
    });
  };

  const columns: ColumnDef<DeliveryListItem>[] = [
    {
      accessorKey: 'workOrderNumber',
      header: 'W/O No.',
      size: 100,
      cell: ({ row }) => {
        return <OneLineText text={row.original.workOrderNumber ?? '-'} className="min-w-[70px] mr-5" />;
      },
    },
    {
      accessorKey: 'clientId',
      header: 'Client #',
      size: 100,
      cell: ({ row }) => {
        return <OneLineText text={row.original.clientCode} className="min-w-[70px] mr-5" />;
      },
    },
    {
      id: 'createdOnDate',
      accessorKey: 'entryDate',
      header: 'Entry Date',
      enableSorting: true,
      cell: ({ row }) => {
        return <OneLineText text={convertISODateToMMDDYYYY(row.original.entryDate)} className="min-w-[100px] mr-5" />;
      },
    },
    {
      accessorKey: 'pickupDriver',
      header: 'Pickup Driver',
      size: 150,
      cell: ({ row }) => {
        return <OneLineText text={row.original.pickupDriver ?? '-'} className="min-w-[100px] mr-5" />;
      },
    },
    {
      accessorKey: 'deliveryDriver',
      header: 'Delivery Driver',
      size: 150,
      cell: ({ row }) => {
        return <OneLineText text={row.original.deliveryDriver ?? '-'} className="min-w-[100px] mr-5" />;
      },
    },
    {
      accessorKey: 'assignedUser',
      header: 'Assigned User',
      size: 150,
      cell: ({ row }) => {
        return <OneLineText text={row.original.assignedUser ?? '-'} className="min-w-[100px] mr-5" />;
      },
    },
    {
      accessorKey: 'totalFee',
      header: 'Fee',
      size: 100,
      cell: ({ row }) => {
        return <OneLineText text={`$${row.original.totalFee.toFixed(2)}`} className="min-w-[70px] mr-5" />;
      },
    },
    {
      accessorKey: 'status',
      header: 'Status',
      size: 200,
      cell: ({ row }) => {
        const status = row.original.status;
        return (
          <div
            className={`h-[20px] w-[140px] text-xs font-medium mr-5 rounded-full flex items-center justify-center ${statusClassMap[status] ?? ''}`}
          >
            {DELIVERY_STATUS_OPTIONS.find((opt) => opt.value === status)?.label ?? status}
          </div>
        );
      },
    },
    {
      accessorKey: 'actions',
      header: 'Actions',
      size: 200,
      cell: ({ row }) => {
        const allowedActions = row.original.allowedActions;
        const isDraft = row.original.isDraft;
        const status = row.original.status;
        const type = row.original.type;

        // Determine available menu options based on requirements
        const getMenuOptions = () => {
          const options: {
            id: string;
            label: string;
            action: (e: React.MouseEvent) => void;
            isDestructive?: boolean;
          }[] = [];

          // Helper to add option with permission check
          const addOption = (
            id: string,
            label: string,
            action: (e: React.MouseEvent) => void,
            permission?: string,
            isDestructive = false
          ) => {
            if (permission && !hasFeature(permission)) return;
            options.push({ id, label, action, isDestructive });
          };

          if (type === 'INTAKE') {
            // Intake Form Context
            // Options: Convert to Work Order, Assign to Me, Cancel Delivery
            if (status === 'Draft') {
              addOption(
                'CONVERT_TO_WORK_ORDER',
                'Convert to Work Order',
                () => {
                  if (row.original.id) void handleConvertToWorkOrder(row.original.id);
                },
                'delivery.workOrder.create'
              );
            }

            if (canAssignMe) {
              addOption('ASSIGN_TO_ME', 'Assign to Me', (e: React.MouseEvent) => {
                e?.stopPropagation(); // Handle event if passed
                const intakeId = row.original.intakeId ?? row.original.id;
                void handleAssignToMe(undefined, intakeId);
              });
            }

            if (status !== 'Cancelled') {
              addOption(
                'CANCEL_DELIVERY',
                'Cancel Delivery',
                (e: React.MouseEvent) => {
                  e?.stopPropagation();
                  const intakeId = row.original.intakeId ?? row.original.id;
                  handleCancelDelivery(undefined, intakeId);
                },
                'delivery.edit.status',
                true
              );
            }
          } else {
            // Work Order Context
            const assignToMe = () => {
              if (!canAssignMe) return;
              addOption('ASSIGN_TO_ME', 'Assign to Me', (e: React.MouseEvent) => {
                e?.stopPropagation();
                const workOrderId = row.original.workOrderId ?? row.original.id;
                void handleAssignToMe(workOrderId, undefined);
              });
            };

            const rescheduleDelivery = () =>
              addOption(
                'RESCHEDULE_DELIVERY',
                'Reschedule Delivery',
                (e: React.MouseEvent) => {
                  e?.stopPropagation();
                  // const workOrderId = row.original.workOrderId ?? row.original.id;
                  handleRescheduleWorkOrder();
                },
                'delivery.workOrder.reschedule'
              );

            const cancelDelivery = () =>
              addOption(
                'CANCEL_DELIVERY',
                'Cancel Delivery',
                (e: React.MouseEvent) => {
                  e?.stopPropagation();
                  const workOrderId = row.original.workOrderId ?? row.original.id;
                  handleCancelDelivery(workOrderId, undefined);
                },
                'delivery.edit.status',
                true
              );

            switch (status) {
              case 'Unassigned':
                assignToMe();
                rescheduleDelivery();
                cancelDelivery();
                break;
              case 'Assigned':
                assignToMe();
                rescheduleDelivery();
                cancelDelivery();
                break;
              case 'OutForDelivery':
                assignToMe();
                cancelDelivery();
                break;
              case 'Delivered':
                assignToMe();
                break;
              case 'Billed':
                assignToMe();
                break;
              case 'Rescheduled':
                assignToMe();
                cancelDelivery();
                break;
              case 'Cancelled':
                assignToMe();
                break;
            }
          }
          return options;
        };

        const menuOptions = getMenuOptions();

        return (
          <div
            className="w-fit flex cursor-pointer min-w-[40px] flex-row gap-4 items-center"
            onClick={(e) => {
              e.stopPropagation();
            }}
            role="button"
          >
            {/* Edit Icon */}
            {((isDraft && allowedActions.includes('EDIT_INTAKE_FORM') && hasFeature('delivery.edit')) ||
              (!isDraft && allowedActions.includes('EDIT_WORK_ORDER') && hasFeature('delivery.workOrder.edit'))) && (
              <Edit3
                className="h-5 w-5 text-primary cursor-pointer"
                onClick={() => {
                  if (isDraft) {
                    navigate(`/delivery/${row.original.intakeId ?? row.original.id}/edit`);
                  } else {
                    navigate(`/delivery/work-order/${row.original.workOrderId ?? row.original.id}/edit`);
                  }
                }}
              />
            )}

            {/* Export Delivery Sheet Icon */}
            {allowedActions.includes('EXPORT_DELIVERY_SHEET') && hasFeature('delivery.export.deliverySheet') && (
              <Download
                className="h-5 w-5 text-primary cursor-pointer"
                onClick={async () => {
                  const workOrderId =
                    row.original.workOrderId ?? (row.original.type === 'WORK_ORDER' ? row.original.id : null);
                  if (!workOrderId) {
                    toast({
                      variant: 'destructive',
                      description: 'Work order ID not found.',
                      duration: 3000,
                    });
                    return;
                  }

                  const { data, error } = await deliveryService.exportDeliverySheet(workOrderId);
                  if (error) {
                    displayError(error, 5000);
                  } else if (data) {
                    const fileName = `${row.original.workOrderNumber ?? 'delivery-sheet'}.pdf`;
                    downloadFilefromBuffer(data, fileName, 'PDF');
                  }
                }}
              />
            )}

            {/* Comments Icon */}
            {!isDraft && hasFeature('delivery.workOrder.comments') && (
              <div className="relative">
                <MessageSquarePlus
                  className="h-5 w-5 text-primary cursor-pointer"
                  onClick={() => {
                    setOpenCommentSection(true);
                    setSelectedWorkOrderId(
                      row.original.workOrderId ?? (row.original.type === 'WORK_ORDER' ? row.original.id : null)
                    );
                  }}
                />
                {row.original.commentCount > 0 ? (
                  <span className="absolute -top-2 -right-2 flex items-center justify-center min-w-[16px] h-[16px] px-1 text-[8px] font-semibold text-white bg-primary rounded-full">
                    {row.original.commentCount}
                  </span>
                ) : null}
              </div>
            )}

            {/* More Menu */}
            {menuOptions.length > 0 && (
              <DropdownMenu modal={false}>
                <DropdownMenuTrigger asChild>
                  <div className="cursor-pointer">
                    <Ellipsis color="#676767" />
                  </div>
                </DropdownMenuTrigger>
                <DropdownMenuContent className="bg-white border-[1px] border-bordercolor z-[1000] rounded-md max-h-[300px] overflow-auto custom-scrollbar">
                  {menuOptions.map((option, index) => (
                    <div key={option.id}>
                      <DropdownMenuItem
                        className={`!opacity-100 cursor-pointer ${
                          option.isDestructive ? 'text-red-600' : '!font-semibold'
                        }`}
                        onClick={(e) => option.action(e)}
                      >
                        {option.label}
                      </DropdownMenuItem>
                      {option.id === 'CONVERT_TO_WORK_ORDER' && index < menuOptions.length - 1 && (
                        <DropdownMenuSeparator />
                      )}
                    </div>
                  ))}
                </DropdownMenuContent>
              </DropdownMenu>
            )}
          </div>
        );
      },
    },
  ];

  const handleSearch = (searchParams: Record<string, string | string[] | Date | undefined> | undefined) => {
    if (!searchParams) {
      setDeliveryFilters([]);
      return;
    }

    const updatedFilters = [...useFilterStore.getState().deliveryFilters];

    Object.entries(searchParams).forEach(([field, value]) => {
      if (Array.isArray(value) || value instanceof Date) {
        return;
      }

      const existingIndex = updatedFilters.findIndex((f) => f.field === field);

      if (!value) {
        if (existingIndex !== -1) {
          updatedFilters.splice(existingIndex, 1);
        }
        return;
      }

      const fieldType = DELIVERY_ID_SEARCH_FIELDS.has(field)
        ? AdvanceFilterFieldType.ID
        : DELIVERY_TEXT_SEARCH_FIELDS.has(field)
          ? AdvanceFilterFieldType.STRING
          : AdvanceFilterFieldType.ID;
      const operator =
        fieldType === AdvanceFilterFieldType.STRING
          ? AdvanceFilterOperator.CONTAINS
          : AdvanceFilterOperator.EQUALS;

      const filterEntry = { field, value, operator, fieldType };

      if (existingIndex !== -1) {
        updatedFilters[existingIndex] = filterEntry;
      } else {
        updatedFilters.push(filterEntry);
      }
    });

    setDeliveryFilters(updatedFilters);
  };

  return (
    <div className="w-full h-full">
      <div className="flex w-[100%]">
        <FormProvider {...methods}>
          <DataTable
            columns={columns}
            apiGet={getDeliveryListData}
            showPagination={true}
            onRowClick={handleRowClick}
            search={deliverySearchFields}
            onSearch={handleSearch}
            refreshKey={refreshKey}
            toolbar={{
              toolBarTitle: 'Delivery Service',
              addButton: hasFeature('delivery.create'),
              addButtonTitle: 'New Delivery',
              addOnClick: () => navigate('/delivery/create'),
              serviceSwitcher: <ServiceSwitcher currentService="delivery" />,
            }}
            advanceFilter={{
              filters: deliveryFilters,
              setIsAdvanceFilterOpen: setIsAdvanceFilterOpen,
            }}
          />
        </FormProvider>

        {/* Comments Dialog */}
        {openCommentSection && selectedWorkOrderId && (
          <DeliveryCommentsDialog
            workOrderId={selectedWorkOrderId}
            open={openCommentSection}
            onClose={() => {
              setOpenCommentSection(false);
              setSelectedWorkOrderId(null);
            }}
            onCommentAdded={() => setRefreshKey((prev) => prev + 1)}
          />
        )}

        {/* Delivery Details Side Panel */}
        {selectedDelivery && (
          <DeliveryDetails
            id={selectedDelivery.id}
            fallbackClientName={selectedDelivery.clientName}
            onCloseClick={() => {
              setSelectedDelivery(null);
              setSearchParams({});
            }}
          />
        )}

        <DeliveryAdvanceFilters
          onClose={() => setIsAdvanceFilterOpen(false)}
          filters={deliveryFilters}
          setFilters={setDeliveryFilters}
          deliveryListViewFilters={deliverySearchFields.map((field) => field.name)}
          open={isAdvanceFilterOpen}
        />

        <CancellationReasonDialog
          open={cancellationDialog.open}
          onClose={() => setCancellationDialog({ open: false, id: null, type: null })}
          onConfirm={onConfirmCancellation}
          itemType={cancellationDialog.type === 'WORK_ORDER' ? 'Work Order' : 'Intake Form'}
          isLoading={isCancelling}
        />
      </div>
    </div>
  );
};

export default DeliveryList;
